import dayjs from 'dayjs';
import {
  CanAccess,
  CrudFilters,
  HttpError,
  useNotification
} from '@refinedev/core';
import { CorporateReportFilterForm } from './components/CorporateReportFilterForm';
import { DatePicker, Form, Select, Table } from 'antd';
import { ENROLLMENT_TYPE_OPTIONS } from './constants';
import { ExportButton, TextField, useTable } from '@refinedev/antd';
import { ICourseCompletion } from 'interfaces/corporate_reports';
import { ListLayout } from 'components/layout/layout/ListLayout';
import { useCallback, useEffect, useState } from 'react';
import { useCorporateCourseOptions } from './hooks/useCorporateCourseOptions';
import { useCorporateReportExport } from './hooks/useCorporateReportExport';
import { validateEnrollmentTypeSelection } from './utils/filters';

interface IFilter {
  by_course_enrollment_type?: string;
  by_course_id?: string[];
  by_range_date?: [dayjs.Dayjs, dayjs.Dayjs];
}

const REPORT_STORAGE_KEY = 'course_completion_id';
const getEnrollmentTypeLabel = (value?: string) =>
  ENROLLMENT_TYPE_OPTIONS.find(option => option.value === value)?.label ||
  value ||
  '-';

export const CourseCompletion = () => {
  const [hasSearched, setHasSearched] = useState(false);
  const notification = useNotification();
  const { RangePicker } = DatePicker;

  const { tableProps, searchFormProps } = useTable<
    ICourseCompletion,
    HttpError,
    IFilter
  >({
    resource: 'course_completions',
    queryOptions: { enabled: hasSearched },
    onSearch: params => {
      const filters: CrudFilters = [];

      filters.push(
        {
          field: 'filter[by_course_enrollment_type]',
          operator: 'eq',
          value: params.by_course_enrollment_type
        },
        {
          field: 'filter[by_course_id][]',
          operator: 'in',
          value: params.by_course_id
        },
        {
          field: 'filter[by_range_date][]',
          operator: 'between',
          value: [
            params.by_range_date?.[0]?.format('YYYY-MM-DD'),
            params.by_range_date?.[1]?.format('YYYY-MM-DD')
          ]
        }
      );

      return filters;
    },
    sorters: {
      mode: 'server'
    }
  });
  const selectedEnrollmentType = Form.useWatch(
    'by_course_enrollment_type',
    searchFormProps.form
  );
  const tablePropsWithConditionalData = hasSearched
    ? tableProps
    : { ...tableProps, dataSource: [], loading: false };
  const { courseOptions } = useCorporateCourseOptions(selectedEnrollmentType);

  useEffect(() => {
    searchFormProps.form?.setFieldValue('by_course_id');
  }, [searchFormProps.form, selectedEnrollmentType]);

  const { reportId, handleExportReport } = useCorporateReportExport<IFilter>({
    storageKey: REPORT_STORAGE_KEY,
    reportPath: '/admin/course_completions/report',
    searchFormProps,
    notification,
    buildExportFilters: formValues => formValues
  });

  const validateFilter = useCallback(() => {
    return validateEnrollmentTypeSelection(
      searchFormProps.form?.getFieldValue('by_course_enrollment_type'),
      () =>
        notification.open?.({
          type: 'error',
          message: 'Modalidade obrigatória',
          description: 'Selecione a modalidade para aplicar o filtro.'
        })
    );
  }, [notification, searchFormProps.form]);

  return (
    <ListLayout
      title="Encerramento de Curso"
      headerActions={
        <CanAccess resource="corporate_reports/course_completion" action="list">
          <ExportButton
            onClick={handleExportReport}
            disabled={!!reportId || !hasSearched || !selectedEnrollmentType}
            loading={!!reportId}
          >
            {!!reportId ? 'Relatório está sendo gerado' : 'Exportar'}
          </ExportButton>
        </CanAccess>
      }
    >
      <CorporateReportFilterForm
        searchFormProps={searchFormProps}
        tableLoading={!!tableProps.loading}
        setHasSearched={setHasSearched}
        onValidateFilter={validateFilter}
      >
        <Form.Item className="mb-0 w-45" help={null}>
          <Form.Item
            name="by_course_enrollment_type"
            noStyle
            rules={[{ required: true, message: '' }]}
          >
            <Select
              allowClear
              placeholder="Modalidade"
              options={ENROLLMENT_TYPE_OPTIONS}
            />
          </Form.Item>
        </Form.Item>

        <Form.Item name="by_course_id" className="mb-0 w-45">
          <Select
            mode="multiple"
            allowClear
            disabled={!selectedEnrollmentType}
            style={{ width: '100%' }}
            placeholder="Curso"
            {...courseOptions}
          />
        </Form.Item>
        <Form.Item name="by_range_date">
          <RangePicker format="DD/MM/YYYY" />
        </Form.Item>
      </CorporateReportFilterForm>

      <Table
        {...tablePropsWithConditionalData}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          ...tablePropsWithConditionalData.pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} itens`
        }}
      >
        <Table.Column
          dataIndex="course_enrollment_type"
          key="course_enrollment_type"
          title="Modalidade"
          width={140}
          render={value => <TextField value={getEnrollmentTypeLabel(value)} />}
        />
        <Table.Column
          dataIndex="course_name"
          key="course_name"
          title="Curso"
          sorter
          className="min-w-[120px] max-w-[300px]"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="total_enrolled"
          key="total_enrolled"
          title="Total de matriculados"
          sorter
          width={120}
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="completed_count"
          key="completed_count"
          title="Concluintes"
          sorter
          width={120}
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="not_completed_count"
          key="not_completed_count"
          title="Não concluintes"
          sorter
          width={150}
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="locked_count"
          key="locked_count"
          title="Trancados"
          sorter
          width={120}
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="completion_percentage"
          key="completion_percentage"
          title="Percentual de conclusão"
          sorter
          width={140}
          render={value => <TextField value={`${value}%`} />}
        />
      </Table>
    </ListLayout>
  );
};
